/**
 * Segurança do login (rate limit) — store React Query. I-2.
 *
 * Contrato: /api/v1/sistema/seguranca-login (só admin, porteiro no servidor)
 *   GET  /            → { config, bloqueios }
 *   PUT  /config      → { config }
 *   POST /destravar   → { ok, tentativasRemovidas, bloqueios }
 *
 * Segue o padrão do horario-store (React Query + api client).
 *
 * ZERO localStorage: os limites são regra do servidor. Guardar cópia no
 * navegador só criaria uma segunda verdade — e a que o atacante controla.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';

export interface LoginRateConfig {
  contaLimite: number;
  contaJanelaMin: number;
  contaBloqueioMin: number;
  ipLimite: number;
  ipJanelaMin: number;
  ipBloqueioMin: number;
  atrasoAPartirDe: number;
  atrasoPassoMs: number;
  atrasoMaxMs: number;
  respostaMinMs: number;
  cofreContaLimite: number;
  cofreContaJanelaMin: number;
  cofreContaBloqueioMin: number;
  cofreIpLimite: number;
  cofreIpJanelaMin: number;
  cofreIpBloqueioMin: number;
  notificarAdminApos: number;
  retencaoHoras: number;
}

export interface BloqueioAtivo {
  escopo: string;
  chaveTipo: 'conta' | 'ip';
  chave: string;
  ateMs: number;
  segundosRestantes: number;
  ip: string | null;
}

interface RespostaSeguranca {
  config: LoginRateConfig;
  bloqueios: BloqueioAtivo[];
}

const KEY = ['v1', 'sistema', 'seguranca-login'] as const;

export function useSegurancaLogin() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<RespostaSeguranca>('/api/v1/sistema/seguranca-login'),
    enabled: hasToken(),
    // 30 s: a lista de bloqueios é operacional — o admin costuma abrir a tela
    // justamente porque alguém ligou dizendo que não consegue entrar.
    staleTime: 30_000,
  });
}

export function useSegurancaLoginActions() {
  const qc = useQueryClient();
  const invalidar = () => qc.invalidateQueries({ queryKey: KEY });

  const salvarConfig = useMutation({
    mutationFn: (parcial: Partial<LoginRateConfig>) =>
      api.put<{ config: LoginRateConfig }>('/api/v1/sistema/seguranca-login/config', parcial),
    onSuccess: () => void invalidar(),
  });

  const destravar = useMutation({
    mutationFn: (alvo: { escopo: string; chaveTipo: 'conta' | 'ip'; chave: string }) =>
      api.post<{ ok: boolean; tentativasRemovidas: number; bloqueios: BloqueioAtivo[] }>(
        '/api/v1/sistema/seguranca-login/destravar',
        alvo,
      ),
    onSuccess: () => void invalidar(),
  });

  return { salvarConfig, destravar };
}

/** Rótulo legível da superfície — o slug técnico não diz nada pro admin. */
export function rotuloEscopo(escopo: string): string {
  const mapa: Record<string, string> = {
    'igm-login': 'Login da equipe IGM',
    'cliente-login': 'Portal do cliente (gestor e portaria)',
    'cliente-reset': 'Portal do cliente — esqueci minha senha',
    'cliente-convite': 'Portal do cliente — convite e nova senha',
    'terceiro-login': 'Portal do terceiro',
    'terceiro-reset': 'Portal do terceiro — esqueci minha senha',
    'terceiro-convite': 'Portal do terceiro — convite e nova senha',
    'cofre-revelar': 'Cofre — revelar senha',
  };
  return mapa[escopo] ?? escopo;
}
